import { useEffect, useRef, useState } from "react";
import type { LiveSessionItem } from "../../../shared/types";
import { AtlShell } from "../../components/atelier";
import { Ico } from "../../components/shell";
import { api, ApiError } from "../../api";
import { alertDialog, confirmDialog } from "../../components/Dialog";

const PHASE_LABEL: Record<string, string> = {
  lobby: "In lobby",
  question: "Question live",
  reveal: "Revealing answer",
  leaderboard: "Scoreboard",
  podium: "Finishing",
};

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export default function Live() {
  const [sessions, setSessions] = useState<LiveSessionItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  function load() {
    api.listLiveSessions().then((r) => setSessions(r.sessions)).catch(() => setSessions([]));
  }
  useEffect(() => {
    load();
    timer.current = window.setInterval(load, 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, []);

  async function end(s: LiveSessionItem) {
    const ok = await confirmDialog({
      title: "End this game?",
      message: `“${s.quizTitle}” (PIN ${s.pin}, hosted by ${s.hostName}) will be force-ended — players are sent to the podium and the session is saved.`,
      tone: "danger",
      confirmLabel: "End game",
    });
    if (!ok) return;
    setBusy(s.hostId);
    try {
      await api.endLiveSession(s.hostId);
      load();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not end the session");
    } finally {
      setBusy(null);
    }
  }

  return (
    <AtlShell active="Live" title="Live" sub="GAMES RUNNING RIGHT NOW">
      <div style={{ padding: 30, display: "flex", flexDirection: "column", gap: 16, maxWidth: 940 }}>
        <div className="atl-card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap", rowGap: 10 }}>
            <div style={{ flex: "1 1 180px" }}>
              <h3 className="atl-serif" style={{ fontSize: 19 }}>
                Live sessions {sessions ? <span style={{ color: "var(--atl-faint)" }}>({sessions.length})</span> : ""}
              </h3>
              <p style={{ fontSize: 13, color: "var(--atl-soft)", fontWeight: 500, marginTop: 2 }}>
                Auto-refreshing every 5s. End a game to clear a stuck or errored session.
              </p>
            </div>
            <button className="atl-iconbtn" title="Refresh now" onClick={load}>
              <Ico name="clock" size={15} />
            </button>
          </div>

          {sessions === null ? (
            <p style={{ fontSize: 13, color: "var(--atl-faint)", fontWeight: 500 }}>Loading…</p>
          ) : sessions.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--atl-faint)", fontWeight: 500 }}>No games are running right now.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sessions.map((s) => (
                <div key={s.hostId} className="atl-row" style={{ padding: "12px 16px", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <span
                    style={{
                      fontFamily: "var(--mono, ui-monospace, monospace)",
                      fontWeight: 700,
                      fontSize: 16,
                      letterSpacing: ".06em",
                      padding: "6px 10px",
                      borderRadius: 9,
                      background: "var(--atl-surface-2)",
                      border: "1px solid var(--atl-line)",
                      flex: "0 0 auto",
                    }}
                  >
                    {s.pin}
                  </span>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontWeight: 600, fontSize: 14.5, overflowWrap: "anywhere" }}>{s.quizTitle}</div>
                    <div style={{ fontSize: 12.5, color: "var(--atl-faint)", fontWeight: 500 }}>
                      {s.hostName} · running {fmtElapsed(s.startedAt)}
                      {typeof s.playerCount === "number" ? ` · ${s.playerCount} baker${s.playerCount === 1 ? "" : "s"}` : ""}
                    </div>
                  </div>
                  <span
                    className="atl-chip"
                    style={{ borderColor: "#cdddee", color: "#3b6aa0", background: "#eef3fa", fontWeight: 600 }}
                  >
                    {s.phase ? PHASE_LABEL[s.phase] ?? s.phase : "Live"}
                  </span>
                  <button
                    className="atl-btn atl-btn--ghost"
                    style={{ padding: "9px 16px", fontSize: 13, color: "var(--atl-accent)" }}
                    disabled={busy === s.hostId}
                    onClick={() => end(s)}
                  >
                    {busy === s.hostId ? "Ending…" : "End game"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AtlShell>
  );
}
